import React, { useState, useCallback } from 'react';
import { MdAdd, MdCheck } from 'react-icons/md';
import '../../styles/shared/AddToCartButton.css';

interface Props {
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

const CONFETTI_COLORS = ['#10B981', '#34D399', '#FBBF24', '#F59E0B', '#3B82F6', '#6366F1', '#EF4444', '#EC4899', '#8B5CF6', '#14B8A6'];

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  angle: number;
  scale: number;
  shape: 'circle' | 'rect' | 'star';
  delay: number;
}

const AddToCartButton: React.FC<Props> = ({ onClick, disabled, size = 'md' }) => {
  const [added, setAdded] = useState(false);
  const [confetti, setConfetti] = useState<Particle[]>([]);

  const spawnConfetti = useCallback(() => {
    const particles: Particle[] = Array.from({ length: 24 }, (_, i) => {
      const angle = (i / 24) * 360 + (Math.random() - 0.5) * 30;
      const distance = 60 + Math.random() * 100;
      const rad = (angle * Math.PI) / 180;
      return {
        id: Date.now() + i,
        x: Math.cos(rad) * distance,
        y: Math.sin(rad) * distance - 20,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        angle: Math.random() * 720,
        scale: 0.6 + Math.random() * 0.8,
        shape: (['circle', 'rect', 'star'] as const)[i % 3],
        delay: Math.random() * 0.1,
      };
    });
    setConfetti(particles);
    setTimeout(() => setConfetti([]), 1200);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || added) return;
    onClick(e);
    setAdded(true);
    spawnConfetti();
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="atc-wrap">
      {confetti.map((p) => (
        <span
          key={p.id}
          className={`atc-confetti atc-confetti--${p.shape}`}
          style={{
            '--cx': `${p.x}px`,
            '--cy': `${p.y}px`,
            '--cr': `${p.angle}deg`,
            '--cs': p.scale,
            '--cd': `${p.delay}s`,
            background: p.color,
          } as React.CSSProperties}
        />
      ))}
      <button
        className={`atc-btn atc-btn--${size} ${added ? 'atc-btn--added' : ''}`}
        onClick={handleClick}
        disabled={disabled}
        aria-label="Add to cart"
      >
        {added ? <MdCheck /> : <MdAdd />}
      </button>
    </div>
  );
};

export default AddToCartButton;
